import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Divider,
  TextField,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import {
  GetUserGroups,
  AssignSecurityGroup,
  UnassignSecurityGroup,
} from "../../../services/UsersService";
import CommonLoader from "../../common/CommonLoader";

const AccessGroups = ({ userId: userIdProp, embedded }) => {
  const params = useParams();
  const navigate = useNavigate();
  const userId = userIdProp || params.userId;

  const [assigned, setAssigned] = useState([]);
  const [available, setAvailable] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyGroup, setBusyGroup] = useState(null);

  const loadGroups = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await GetUserGroups(userId);
      const results = res.results || {};
      setAssigned(results.assigned || []);
      setAvailable(results.available || []);
    } catch (error) {
      NotificationManager.error("Failed to load access groups.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleAssign = async (securityGroup) => {
    try {
      setBusyGroup(securityGroup);
      await AssignSecurityGroup(userId, securityGroup);
      await loadGroups();
    } catch (error) {
      NotificationManager.error("Failed to assign group.");
    } finally {
      setBusyGroup(null);
    }
  };

  const handleUnassign = async (securityGroup) => {
    try {
      setBusyGroup(securityGroup);
      await UnassignSecurityGroup(userId, securityGroup);
      await loadGroups();
    } catch (error) {
      NotificationManager.error("Failed to remove group.");
    } finally {
      setBusyGroup(null);
    }
  };

  const filteredAvailable = available.filter((g) =>
    g.securityGroup?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderList = (items, actionIcon, onAction, emptyText) => (
    <Paper variant="outlined" sx={{ height: 360, overflowY: "auto" }}>
      <List dense>
        {items.length === 0 && (
          <ListItem>
            <ListItemText
              primary={emptyText}
              primaryTypographyProps={{ color: "text.secondary", fontStyle: "italic" }}
            />
          </ListItem>
        )}
        {items.map((g) => (
          <ListItem
            key={g.securityGroup}
            secondaryAction={
              <IconButton
                edge="end"
                disabled={busyGroup === g.securityGroup}
                onClick={() => onAction(g.securityGroup)}
              >
                {actionIcon}
              </IconButton>
            }
          >
            <ListItemText primary={g.securityGroup} secondary={g.description} />
          </ListItem>
        ))}
      </List>
    </Paper>
  );

  return (
    <>
      {loading && <CommonLoader loading={loading} />}
      <Box sx={{ mt: embedded ? 2 : 0 }}>
        {!embedded && (
          <>
            <span className="title">Access Groups</span>
            <hr />
          </>
        )}
        <Paper elevation={embedded ? 0 : 3} sx={{ p: embedded ? 0 : 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Managing access groups for <strong>{userId}</strong>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Available Security Groups
              </Typography>
              <TextField
                size="small"
                label="Search"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 1 }}
              />
              {renderList(
                filteredAvailable,
                <ArrowForwardIcon color="primary" />,
                handleAssign,
                "No available groups."
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Assigned Groups
              </Typography>
              <TextField
                size="small"
                label="Search"
                fullWidth
                value=""
                disabled
                sx={{ mb: 1, visibility: "hidden" }}
              />
              {renderList(
                assigned,
                <ArrowBackIcon color="error" />,
                handleUnassign,
                "No groups assigned yet."
              )}
            </Grid>
          </Grid>

          {!embedded && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="outlined" onClick={() => navigate("/vlp/settings-users")}>
                  Back to Users
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default AccessGroups;
